/**
 * 角色与权限 —— 管理端 RBAC 的可视化配置入口。
 *
 * 列表里的每个角色都可以查看权限；拥有 `role:write` 的账号还能新建、修改、
 * 删除自定义角色。权限矩阵的分组与中文名来自服务端（`GET /api/roles`），
 * 前端不再维护第二份「能力清单」。
 *
 * 两条护栏在服务端（`server/src/roles.ts`），这里同步把按钮禁掉只是体验优化：
 *   - 内置 admin 角色不可删除，且必须保留账号/角色管理能力；
 *   - 仍有账号占用的角色不可删除。
 */
import { useState } from 'react'
import { Loader2, Lock, Pencil, Plus, ShieldCheck, Trash2, TriangleAlert } from 'lucide-react'
import { toast } from 'sonner'
import { api, useAsync, type Capability, type CapabilityGroup, type RoleRow } from '@/lib/api'
import { useAuth, useCan } from '@/lib/auth'
import { PageHeader } from '@/components/PageHeader'
import { ConfirmDialog } from '@/components/ConfirmDialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

/** 与 server/src/authz.ts 的 ADMIN_PROTECTED_CAPABILITIES 保持一致。 */
const ADMIN_PROTECTED_CAPABILITIES: Capability[] = [
  'user:read',
  'user:write',
  'role:read',
  'role:write',
]

interface EditorState {
  mode: 'create' | 'edit'
  id: string
  name: string
  description: string
  capabilities: Set<Capability>
  /** 打开编辑器时的能力集合，用于判断保存后是否需要刷新当前账号的界面。 */
  original: Set<Capability>
}

function sameSet(a: Set<Capability>, b: Set<Capability>): boolean {
  if (a.size !== b.size) return false
  for (const value of a) if (!b.has(value)) return false
  return true
}

function errorText(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback
}

export function RolesPage() {
  const { user: me } = useAuth()
  const canWrite = useCan()('role:write')
  const list = useAsync(() => api.listRoles(), [])

  const [editor, setEditor] = useState<EditorState | null>(null)
  const [busy, setBusy] = useState(false)
  const [pendingDelete, setPendingDelete] = useState<RoleRow | null>(null)

  const rows = list.data?.rows ?? []
  const groups = (list.data?.meta ?? []).filter((group) => !group.hidden)

  function isProtectedFor(state: EditorState | null, capability: Capability): boolean {
    return (
      state?.mode === 'edit' &&
      state.id === 'admin' &&
      ADMIN_PROTECTED_CAPABILITIES.includes(capability)
    )
  }

  function isProtected(capability: Capability): boolean {
    return isProtectedFor(editor, capability)
  }

  function openCreate() {
    setEditor({
      mode: 'create',
      id: '',
      name: '',
      description: '',
      capabilities: new Set(),
      original: new Set(),
    })
  }

  function openEdit(row: RoleRow) {
    setEditor({
      mode: 'edit',
      id: row.id,
      name: row.name,
      description: row.description ?? '',
      capabilities: new Set(row.capabilities),
      original: new Set(row.capabilities),
    })
  }

  function toggleCapability(capability: Capability) {
    if (!canWrite) return
    setEditor((prev) => {
      if (!prev || isProtectedFor(prev, capability)) return prev
      const next = new Set(prev.capabilities)
      if (next.has(capability)) next.delete(capability)
      else next.add(capability)
      return { ...prev, capabilities: next }
    })
  }

  /** 分组全选/清空；内置 admin 的受保护能力不会被清掉。 */
  function toggleGroup(group: CapabilityGroup, checked: boolean) {
    if (!canWrite) return
    setEditor((prev) => {
      if (!prev) return prev
      const next = new Set(prev.capabilities)
      for (const item of group.items) {
        if (isProtectedFor(prev, item.capability)) continue
        if (checked) next.add(item.capability)
        else next.delete(item.capability)
      }
      return { ...prev, capabilities: next }
    })
  }

  async function save(e: React.FormEvent) {
    e.preventDefault()
    if (!editor || !canWrite) return
    if (!editor.name.trim()) {
      toast.error('请填写角色名称')
      return
    }
    if (editor.mode === 'create' && !editor.id.trim()) {
      toast.error('请填写角色标识')
      return
    }
    setBusy(true)
    const capabilities = [...editor.capabilities]
    try {
      if (editor.mode === 'create') {
        await api.createRole({
          id: editor.id.trim(),
          name: editor.name.trim(),
          description: editor.description.trim() || null,
          capabilities,
        })
        toast.success(`已创建角色「${editor.name.trim()}」`)
      } else {
        await api.updateRole(editor.id, {
          name: editor.name.trim(),
          description: editor.description.trim() || null,
          capabilities,
        })
        toast.success(`已保存角色「${editor.name.trim()}」`)
      }

      const editedSelf =
        editor.mode === 'edit' &&
        editor.id === me?.role &&
        !sameSet(editor.capabilities, editor.original)
      setEditor(null)
      list.reload()
      if (editedSelf) {
        // 当前账号的能力变了：刷新 /auth/me，避免导航与按钮停留在旧权限上。
        // 服务端已把除操作者外的同角色用户踢下线；操作者自己保留令牌，故这里主动刷新。
        toast.info('当前账号的权限已更新，正在刷新界面…')
        window.setTimeout(() => window.location.reload(), 600)
      }
    } catch (err) {
      toast.error(errorText(err, editor.mode === 'create' ? '创建角色失败' : '保存角色失败'))
    } finally {
      setBusy(false)
    }
  }

  async function remove(row: RoleRow) {
    setBusy(true)
    try {
      await api.deleteRole(row.id)
      toast.success(`已删除角色「${row.name}」`)
      setPendingDelete(null)
      list.reload()
    } catch (err) {
      toast.error(errorText(err, '删除角色失败'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="space-y-5">
      <PageHeader
        title="角色与权限"
        actions={
          canWrite ? (
            <Button onClick={openCreate}>
              <Plus className="size-4" />
              新建角色
            </Button>
          ) : null
        }
      />

      {list.error ? (
        <div className="flex items-start gap-2 rounded-md border border-[var(--color-destructive)] p-3 text-xs text-[var(--color-destructive)]">
          <TriangleAlert className="mt-0.5 size-3.5 shrink-0" />
          <span className="flex-1">加载失败：{list.error}</span>
          <Button variant="ghost" size="sm" onClick={list.reload}>
            重试
          </Button>
        </div>
      ) : null}

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>角色</TableHead>
                <TableHead className="w-[180px]">标识</TableHead>
                <TableHead className="w-[100px]">类型</TableHead>
                <TableHead className="w-[90px]">账号数</TableHead>
                <TableHead className="w-[100px]">权限数</TableHead>
                <TableHead className="w-[190px]">操作</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {list.loading && !list.data ? (
                <TableRow className="hover:bg-transparent">
                  <TableCell
                    colSpan={6}
                    className="py-10 text-center text-sm text-[var(--color-muted-foreground)]"
                  >
                    <span className="inline-flex items-center gap-2">
                      <Loader2 className="size-4 animate-spin" />
                      正在加载…
                    </span>
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow className="hover:bg-transparent">
                  <TableCell
                    colSpan={6}
                    className="py-10 text-center text-sm text-[var(--color-muted-foreground)]"
                  >
                    暂无角色
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => {
                  const deleteBlocked = row.builtin === 1 || row.user_count > 0
                  return (
                    <TableRow key={row.id}>
                      <TableCell>
                        <div className="font-medium">{row.name}</div>
                        {row.description ? (
                          <div className="mt-0.5 max-w-[28rem] text-xs text-[var(--color-muted-foreground)]">
                            {row.description}
                          </div>
                        ) : null}
                      </TableCell>
                      <TableCell className="font-mono-token text-xs">{row.id}</TableCell>
                      <TableCell>
                        <Badge variant={row.builtin === 1 ? 'default' : 'outline'}>
                          {row.builtin === 1 ? '内置' : '自定义'}
                        </Badge>
                      </TableCell>
                      <TableCell className="tabular-nums">{row.user_count}</TableCell>
                      <TableCell className="tabular-nums">{row.capabilities.length}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap items-center gap-1.5">
                          <Button variant="outline" size="sm" onClick={() => openEdit(row)}>
                            <Pencil className="size-3.5" />
                            {canWrite ? '编辑' : '查看'}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-[var(--color-destructive)] hover:text-[var(--color-destructive)]"
                            disabled={!canWrite || deleteBlocked}
                            title={
                              row.builtin === 1
                                ? '内置角色不能删除'
                                : row.user_count > 0
                                  ? `该角色仍有 ${row.user_count} 个用户，请先调整这些用户的角色`
                                  : undefined
                            }
                            onClick={() => setPendingDelete(row)}
                          >
                            <Trash2 className="size-3.5" />
                            删除
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  )
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <p className="flex items-start gap-1.5 text-xs text-[var(--color-muted-foreground)]">
        <ShieldCheck className="mt-0.5 size-3.5 shrink-0" />
        角色权限保存后立即在服务端生效。内置管理员角色必须保留账号与角色管理能力，
        以保证系统始终有恢复入口。
      </p>

      {/* 新建 / 编辑角色 */}
      <Dialog open={editor !== null} onOpenChange={(open) => !open && !busy && setEditor(null)}>
        <DialogContent className="max-h-[85vh] max-w-3xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editor?.mode === 'create' ? '新建角色' : `编辑角色 · ${editor?.name ?? ''}`}
            </DialogTitle>
            <DialogDescription>
              勾选该角色允许执行的操作。能力清单由服务端定义，保存后立即生效。
            </DialogDescription>
          </DialogHeader>

          {editor ? (
            <form className="space-y-4" onSubmit={save}>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="role-id">角色标识</Label>
                  <Input
                    id="role-id"
                    className="font-mono-token"
                    value={editor.id}
                    onChange={(e) => setEditor({ ...editor, id: e.target.value })}
                    placeholder="如 line-operator"
                    disabled={editor.mode === 'edit'}
                    autoFocus={editor.mode === 'create'}
                  />
                  <p className="text-[11px] text-[var(--color-muted-foreground)]">
                    2–32 位小写字母/数字/下划线/连字符，创建后不可修改。
                  </p>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="role-name">角色名称</Label>
                  <Input
                    id="role-name"
                    value={editor.name}
                    onChange={(e) => setEditor({ ...editor, name: e.target.value })}
                    placeholder="如 产线操作员"
                    disabled={!canWrite}
                    autoFocus={editor.mode === 'edit'}
                  />
                </div>

                <div className="space-y-1.5 sm:col-span-2">
                  <Label htmlFor="role-description">说明（可选）</Label>
                  <Input
                    id="role-description"
                    value={editor.description}
                    onChange={(e) => setEditor({ ...editor, description: e.target.value })}
                    placeholder="这个角色适合谁、能做什么"
                    disabled={!canWrite}
                  />
                </div>
              </div>

              <div className="space-y-3">
                {groups.map((group) => (
                  <div key={group.key} className="rounded-md border border-[var(--color-border)]">
                    <div className="flex flex-wrap items-center justify-between gap-2 border-b border-[var(--color-border)] px-3 py-2">
                      <div className="text-sm font-medium">{group.label}</div>
                      <div className="flex items-center gap-1">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          disabled={!canWrite}
                          onClick={() => toggleGroup(group, true)}
                        >
                          全选
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          disabled={!canWrite}
                          onClick={() => toggleGroup(group, false)}
                        >
                          清空
                        </Button>
                      </div>
                    </div>
                    <div className="grid gap-3 p-3 sm:grid-cols-2">
                      {group.items.map((item) => {
                        const locked = isProtected(item.capability)
                        return (
                          <div key={item.capability} className="flex items-start gap-2.5">
                            <Switch
                              checked={editor.capabilities.has(item.capability)}
                              onCheckedChange={() => toggleCapability(item.capability)}
                              disabled={!canWrite || locked}
                              className="mt-0.5"
                            />
                            <div className="min-w-0 space-y-0.5">
                              <div className="flex items-center gap-1 text-sm">
                                {item.label}
                                {locked ? (
                                  <Lock
                                    className="size-3 text-[var(--color-muted-foreground)]"
                                    aria-label="内置管理员必须保留"
                                  />
                                ) : null}
                              </div>
                              {item.hint ? (
                                <div className="text-[11px] text-[var(--color-muted-foreground)]">
                                  {item.hint}
                                </div>
                              ) : null}
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                ))}
              </div>

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  disabled={busy}
                  onClick={() => setEditor(null)}
                >
                  取消
                </Button>
                <Button type="submit" disabled={busy || !canWrite}>
                  {busy ? <Loader2 className="size-4 animate-spin" /> : null}
                  {editor.mode === 'create' ? '创建' : '保存'}
                </Button>
              </DialogFooter>
            </form>
          ) : null}
        </DialogContent>
      </Dialog>

      {pendingDelete ? (
        <ConfirmDialog
          title="删除角色"
          description={`确定删除角色「${pendingDelete.name}」？该角色没有关联账号，此操作不可撤销。`}
          confirmLabel="删除"
          danger
          busy={busy}
          onCancel={() => setPendingDelete(null)}
          onConfirm={() => {
            const target = pendingDelete
            setPendingDelete(null)
            void remove(target)
          }}
        />
      ) : null}
    </div>
  )
}
